import { computed, isRef, reactive, ref, unref, watch } from 'vue';
import { useServiceStore } from '../stores/service.store';
import { formValuesToCreatePayload, formValuesToUpdatePayload, normalizeApiErrors, normalizePhotoStorageIds, serviceToFormValues, validateServiceFormValues, } from '../models/service-form.mapper';
import { overbookingService } from '@domains/scheduling/services/overbooking.service';
import { translateApp } from "@/i18n";
export function useServiceEditor(options) {
    const serviceStore = useServiceStore();
    const serviceRef = isRef(options.service) ? options.service : ref(options.service ?? null);
    const form = reactive(serviceToFormValues(unref(serviceRef)));
    const errors = ref([]);
    const isSubmitting = ref(false);
    const isDirty = ref(false);
    const submitError = ref(null);
    let isHydratingForm = false;
    const isEditMode = computed(() => Number(unref(serviceRef)?.id) > 0);
    const serviceId = computed(() => Number(unref(serviceRef)?.id) || null);
    const buttonText = computed(() => {
        if (isSubmitting.value) {
            return isEditMode.value
                ? translateApp("services.auto.actualizando")
                : translateApp("services.auto.guardando");
        }
        return isEditMode.value
            ? translateApp("services.auto.actualizar")
            : translateApp("services.auto.guardar");
    });
    watch(serviceRef, (service) => {
        isHydratingForm = true;
        Object.assign(form, serviceToFormValues(service));
        errors.value = [];
        submitError.value = null;
        isDirty.value = false;
        isHydratingForm = false;
    }, { immediate: true });
    watch(form, () => {
        if (!isHydratingForm) {
            isDirty.value = true;
        }
    }, { deep: true, flush: 'sync' });
    function markDirty() {
        isDirty.value = true;
    }
    function setPhotoStorageIds(ids) {
        form.photo_storage_ids = normalizePhotoStorageIds(ids);
        markDirty();
    }
    function reset() {
        isHydratingForm = true;
        Object.assign(form, serviceToFormValues(unref(serviceRef)));
        errors.value = [];
        submitError.value = null;
        isDirty.value = false;
        isHydratingForm = false;
    }
    async function save() {
        isSubmitting.value = true;
        errors.value = [];
        submitError.value = null;
        try {
            const validationErrors = validateServiceFormValues(form);
            if (validationErrors.length > 0) {
                errors.value = validationErrors;
                throw new Error(translateApp("services.editor.fix_form_errors"));
            }
            validateOverbooking(options.availability.items.value);
            const savedService = isEditMode.value
                ? await serviceStore.updateService(serviceId.value, { ...formValuesToUpdatePayload(form, form.photo_storage_ids), ...(unref(serviceRef)?.version != null ? { version: unref(serviceRef).version } : {}) })
                : await serviceStore.createService(formValuesToCreatePayload(form, form.photo_storage_ids));
            await saveAvailability(savedService.id);
            isDirty.value = false;
            await options.onSaved?.(savedService);
            return savedService;
        }
        catch (error) {
            if (errors.value.length === 0) {
                errors.value = normalizeApiErrors(error);
            }
            submitError.value =
                errors.value[0]?.form ??
                    (error instanceof Error
                        ? error.message
                        : translateApp("services.validation.save_error"));
            options.onError?.(error);
            throw error;
        }
        finally {
            isSubmitting.value = false;
        }
    }
    async function saveAvailability(savedServiceId) {
        if (!savedServiceId) {
            return;
        }
        const shouldSaveAvailability = options.availability.hasChanges.value
            || (!isEditMode.value && options.availability.items.value.length > 0);
        if (!shouldSaveAvailability) {
            return;
        }
        options.availability.setServiceId(savedServiceId);
        await options.availability.saveAll();
    }
    return {
        form,
        errors,
        isSubmitting,
        isDirty,
        isEditMode,
        serviceId,
        buttonText,
        submitError,
        markDirty,
        setPhotoStorageIds,
        reset,
        save,
    };
}
function validateOverbooking(items) {
    if (!items.length) {
        return;
    }
    const configs = items
        .map((item) => {
        const schedule = item.type === 'availability' ? item.schedule?.[0] : item;
        if (!schedule) {
            return null;
        }
        return {
            priceType: schedule.price_type || 'PERSON',
            standardCapacity: schedule.standard_max_participants || 1,
            overcapacity: schedule.overcapacity_max_participants ?? null,
            overflowPrice: schedule.overflow_price ?? null,
        };
    })
        .filter((config) => config != null);
    const result = overbookingService.validateAll(configs);
    if (!result.valid) {
        throw new Error(result.errors[0]);
    }
}
